import React, { useCallback, useEffect, useState } from "react";

import { useHuntersStyles } from "./index";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableFooter,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from "@material-ui/core";
import ExpandMoreIcon from "@material-ui/icons/ExpandMore";
import StarIcon from "@material-ui/icons/Star";
import ReportProblemIcon from "@material-ui/icons/ReportProblem";
import NumberFormat from "react-number-format";
import { getRopReport } from "./api";
import Progress from "components/UI/Progress";
import EmptyOrShow from "../../components/EmptyOrShow";
import Search from "../../components/UI/Search";

// TZ раздел 5.2 — таблица KPI РОПов (для руководителей): план ОП/факт/%/koef/бонус/streak.
// Доступ уже ограничен на backend (sale.kpi_rop__read через RopReportView): HEAD_HEAD/AUDITOR — все ОП,
// РОП — только своя строка своего отдела.
export const RopKpiTable = ({ filters }) => {
  const classes = useHuntersStyles();
  const [search, setSearch] = useState("");
  const [data, setData] = useState([]);
  const [filteredData, setFilteredData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [stateSum, setStateSum] = useState({});

  const handleSearchChange = useCallback(() => {
    if (!search) return setFilteredData(data);
    setFilteredData(filterData(data, search));
  }, [search, data]);

  useEffect(() => {
    const timeout = setTimeout(() => handleSearchChange(), 500);
    return () => {
      clearTimeout(timeout);
    };
  }, [handleSearchChange]);

  useEffect(() => {
    setStateSum(calcSum(filteredData));
  }, [filteredData]);

  useEffect(() => {
    setLoading(true);
    getRopReport(filters)
      .then((res) => {
        setData(res || []);
        setFilteredData(res || []);
      })
      .catch(() => {})
      .finally(() => {
        setLoading(false);
      });
  }, [filters]);

  const handleSearch = (e) => {
    setSearch(e.target.value);
  };

  return (
    <Box sx={{ marginBottom: "20px", border: "1px solid #ccc", borderRadius: 4 }}>
      <Accordion defaultExpanded style={{ borderRadius: 4, boxShadow: "none" }}>
        <AccordionSummary
          expandIcon={<ExpandMoreIcon />}
          aria-controls="rop-kpi-content"
          id="rop-kpi-header"
        >
          <Typography>KPI РОПов</Typography>
        </AccordionSummary>
        <EmptyOrShow loading={loading}>
          <AccordionDetails style={{ display: "block" }}>
            <div style={{ marginBottom: "25px", width: "250px" }}>
              <Search
                placeholder={"Поиск"}
                handleChange={handleSearch}
                value={search}
              />
            </div>

            <TableContainer className={classes.table_container} style={{ maxHeight: 500 }}>
              <Table size="small" stickyHeader aria-label="sticky table">
                <TableHead className={classes.table_head}>
                  <TableRow>
                    {headTitle.map((el) => {
                      return (
                        <TableCell key={el} style={{ color: "black", fontWeight: "bold" }}>
                          {el}
                        </TableCell>
                      );
                    })}
                  </TableRow>
                </TableHead>
                <TableBody className={classes.table_body}>
                  {filteredData.map((e) => {
                    return (
                      <TableRow key={e.rop_id}>
                        <TableCell align="left">{e.sale_department_name}</TableCell>
                        <TableCell align="left">
                          <Box display="flex" alignItems="center" style={{ gap: 4 }}>
                            {e.fio}
                            {e.is_streak_star ? (
                              <Tooltip title={`Стабильность: план выполнен ${e.streak} мес. подряд`}>
                                <StarIcon fontSize="small" style={{ color: "#FFC107" }} />
                              </Tooltip>
                            ) : null}
                            {e.is_fail_warning ? (
                              <Tooltip title={`Невыполнение плана ${e.fail_streak} мес. подряд`}>
                                <ReportProblemIcon fontSize="small" style={{ color: "#FF6262" }} />
                              </Tooltip>
                            ) : null}
                          </Box>
                        </TableCell>
                        <TableCell align="left">
                          <NumberFormat
                            value={e?.dept_plan}
                            displayType="text"
                            thousandSeparator={" "}
                          />
                        </TableCell>
                        <TableCell align="left">
                          <NumberFormat
                            value={e?.dept_fact}
                            displayType="text"
                            thousandSeparator={" "}
                          />
                        </TableCell>
                        <TableCell align="left">
                          <Progress progress={e.dept_pct} color={e.percentage_color_code} />
                        </TableCell>
                        <TableCell align="left">{e?.rop_koef}</TableCell>
                        <TableCell align="left">
                          <NumberFormat
                            value={e?.rop_bonus}
                            displayType="text"
                            thousandSeparator={" "}
                          />
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
                <TableFooter>
                  <TableRow className={classes.table_footer}>
                    <TableCell style={{ fontWeight: "bold" }}>Итого</TableCell>
                    <TableCell />
                    <TableCell>
                      <NumberFormat
                        value={stateSum?.dept_plan}
                        displayType="text"
                        thousandSeparator={" "}
                      />
                    </TableCell>
                    <TableCell>
                      <NumberFormat
                        value={stateSum?.dept_fact}
                        displayType="text"
                        thousandSeparator={" "}
                      />
                    </TableCell>
                    <TableCell />
                    <TableCell />
                    <TableCell>
                      <NumberFormat
                        value={stateSum?.rop_bonus}
                        displayType="text"
                        thousandSeparator={" "}
                      />
                    </TableCell>
                  </TableRow>
                </TableFooter>
              </Table>
            </TableContainer>
          </AccordionDetails>
        </EmptyOrShow>
      </Accordion>
    </Box>
  );
};

const headTitle = [
  "Отдел продаж",
  "РОП",
  "План ОП",
  "Факт ОП",
  "% выполнения",
  "Коэффициент",
  "Бонус",
];

const calcSum = (filteredData) => {
  return {
    dept_plan: filteredData?.reduce((a, b) => a + (b.dept_plan || 0), 0),
    dept_fact: filteredData?.reduce((a, b) => a + (b.dept_fact || 0), 0),
    rop_bonus: filteredData?.reduce((a, b) => a + (b.rop_bonus || 0), 0),
  };
};

const filterData = (data, search) => {
  return data.filter(
    (el) =>
      el?.sale_department_name?.toLowerCase().includes(search.toLowerCase()) ||
      el?.fio?.toLowerCase().includes(search.toLowerCase())
  );
};
